import { System } from '../../../engine/ecs/core/System';
import {
  GhostComponent,
  GhostMode,
  GhostType,
} from '../components/GhostComponent';
import { PacmanComponent, Direction } from '../components/PacmanComponent';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { MovementComponent } from '../../../engine/ecs/components/MovementComponent';
import { SpriteComponent } from '../../../engine/ecs/components/SpriteComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';
import { MazeData } from '../data/MazeData';
import { Entity } from '../../../engine/ecs/core/Entity';

/**
 * GhostAISystem - AI for ghost entities with grid-based movement like Pacman
 */
export class GhostAISystem extends System {
  private mazeData: any;

  constructor() {
    super();
    this.registerRequiredComponent(GhostComponent);
    this.registerRequiredComponent(TransformComponent);
    this.registerRequiredComponent(MovementComponent);
  }

  /**
   * Set the current maze data for pathfinding
   * This should be called whenever a level is initialized
   */
  public setMazeData(mazeData: any): void {
    this.mazeData = mazeData;
    console.log(
      `[GhostAISystem] Maze data updated (walls: ${mazeData.width}x${mazeData.height})`
    );
  }

  public update(deltaTime: number): void {
    // Require maze data to be set before AI (will be set by PacmanGame on level init)
    if (!this.mazeData) {
      console.warn('[GhostAISystem] No maze data available, using fallback');
      this.mazeData = MazeData.getLevel(1);
    }

    // Debug: Log occasionally to see if system is working
    if (Math.random() < 0.01) {
      console.log(
        `GhostAISystem: Processing ${this.getEntities().length} ghosts`
      );
    }

    // Get Pacman's position for targeting
    const pacmanPosition = this.getPacmanPosition();
    if (!pacmanPosition) {
      console.warn('GhostAISystem: No Pacman position found');
      return;
    }

    // Update each ghost
    for (const entity of this.getEntities()) {
      const ghost = this.componentManager.getComponent(
        entity,
        GhostComponent
      ) as GhostComponent;
      const transform = this.componentManager.getComponent(
        entity,
        TransformComponent
      ) as TransformComponent;
      const movement = this.componentManager.getComponent(
        entity,
        MovementComponent
      ) as MovementComponent;

      // Start ghosts in chase mode if they're in base
      if (ghost.mode === GhostMode.InBase) {
        ghost.mode = GhostMode.Chase;
        console.log(`GhostAISystem: Ghost ${ghost.ghostType} leaving base`);
      }

      this.updateGhostAI(ghost, transform, movement, pacmanPosition, deltaTime);

      // Update sprite rendering data for visual feedback (frightened mode)
      this.updateSpriteRenderingData(entity, ghost, movement);
    }
  }

  /**
   * Update sprite rendering data for ghost visual effects
   */
  private updateSpriteRenderingData(
    entity: Entity,
    ghost: GhostComponent,
    movement: MovementComponent
  ): void {
    const sprite = this.componentManager.getComponent(
      entity,
      SpriteComponent
    ) as SpriteComponent;

    if (!sprite) {
      return;
    }

    const isMoving = movement && movement.velocity.magnitude() > 0.1;
    const isFrightened = ghost.mode === GhostMode.Frightened;

    sprite.renderingData = {
      isMoving: isMoving,
      isFrightened: isFrightened,
    };
  }

  /**
   * Get Pacman's current position
   */
  private getPacmanPosition(): Vector2 | null {
    const allEntities = this.entityManager.getAllEntities();
    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, PacmanComponent)) {
        const transform = this.componentManager.getComponent(
          entity,
          TransformComponent
        ) as TransformComponent;
        return transform.position;
      }
    }
    return null;
  }

  /**
   * Get Pacman's direction for proper targeting
   */
  private getPacmanDirection(): Vector2 {
    const allEntities = this.entityManager.getAllEntities();
    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, PacmanComponent)) {
        const pacman = this.componentManager.getComponent(
          entity,
          PacmanComponent
        ) as PacmanComponent;

        // Convert direction enum to vector
        switch (pacman.currentDirection) {
          case Direction.Up:
            return new Vector2(0, -1);
          case Direction.Down:
            return new Vector2(0, 1);
          case Direction.Left:
            return new Vector2(-1, 0);
          case Direction.Right:
            return new Vector2(1, 0);
          default:
            return new Vector2(0, 0);
        }
      }
    }
    return new Vector2(0, 0);
  }

  /**
   * Update individual ghost AI behavior with grid-based movement
   */
  private updateGhostAI(
    ghost: GhostComponent,
    transform: TransformComponent,
    movement: MovementComponent,
    pacmanPosition: Vector2,
    deltaTime: number
  ): void {
    const cellSize = this.mazeData.cellSize;
    const currentPos = transform.position;

    // Convert current position to grid coordinates
    const gridPos = MazeData.pixelToGrid(currentPos.x, currentPos.y, cellSize);

    // Get target position based on ghost type
    const targetPosition = this.getGhostTarget(
      ghost,
      transform.position,
      pacmanPosition
    );

    // Try to change direction if we're at a grid intersection or need to change
    if (this.shouldChangeDirection(ghost, transform.position, targetPosition)) {
      const newDirection = this.selectBestDirection(
        ghost,
        gridPos,
        targetPosition
      );
      if (newDirection !== Direction.None) {
        ghost.nextDirection = newDirection;
      }
    }

    // Try to change direction immediately if a new direction is requested
    if (ghost.nextDirection !== Direction.None) {
      const nextGridPos = this.getNextGridPosition(
        gridPos,
        ghost.nextDirection
      );
      if (
        MazeData.isValidPosition(
          this.mazeData.maze,
          nextGridPos.x,
          nextGridPos.y
        )
      ) {
        ghost.currentDirection = ghost.nextDirection;
        ghost.nextDirection = Direction.None;
      }
    }

    // Move in current direction if possible (same logic as Pacman)
    if (ghost.currentDirection !== Direction.None) {
      // Calculate movement velocity
      const velocity = this.getDirectionVector(ghost.currentDirection);
      velocity.x *= ghost.speed;
      velocity.y *= ghost.speed;

      movement.velocity = velocity;

      // Calculate potential new position
      let newX = transform.position.x + velocity.x * deltaTime;
      let newY = transform.position.y + velocity.y * deltaTime;

      // Keep ghost centered on the opposite axis of movement (same as Pacman)
      if (
        ghost.currentDirection === Direction.Left ||
        ghost.currentDirection === Direction.Right
      ) {
        // Moving horizontally - keep Y centered on grid
        const currentGridY = Math.round((currentPos.y - 12) / cellSize);
        newY = currentGridY * cellSize + 12;
      } else if (
        ghost.currentDirection === Direction.Up ||
        ghost.currentDirection === Direction.Down
      ) {
        // Moving vertically - keep X centered on grid
        const currentGridX = Math.round((currentPos.x - 12) / cellSize);
        newX = currentGridX * cellSize + 12;
      }

      // Calculate target grid position (same logic as Pacman)
      let targetGridX: number;
      let targetGridY: number;

      switch (ghost.currentDirection) {
        case Direction.Left:
          targetGridX = Math.floor((newX - 12) / cellSize);
          targetGridY = Math.round((newY - 12) / cellSize);
          break;
        case Direction.Right:
          targetGridX = Math.ceil((newX - 12) / cellSize);
          targetGridY = Math.round((newY - 12) / cellSize);
          break;
        case Direction.Up:
          targetGridX = Math.round((newX - 12) / cellSize);
          targetGridY = Math.floor((newY - 12) / cellSize);
          break;
        case Direction.Down:
          targetGridX = Math.round((newX - 12) / cellSize);
          targetGridY = Math.ceil((newY - 12) / cellSize);
          break;
        default:
          targetGridX = Math.round((newX - 12) / cellSize);
          targetGridY = Math.round((newY - 12) / cellSize);
      }

      // Validate target position (same as Pacman)
      const isTargetValid = MazeData.isValidPosition(
        this.mazeData.maze,
        targetGridX,
        targetGridY
      );

      let canMove = true;
      if (
        targetGridX < 0 ||
        targetGridX >= this.mazeData.width ||
        targetGridY < 0 ||
        targetGridY >= this.mazeData.height
      ) {
        canMove = false;
      }

      // Check if new position is valid BEFORE moving there (same as Pacman)
      if (isTargetValid && canMove) {
        // Safe to move - update position
        transform.position.x = newX;
        transform.position.y = newY;
      } else {
        // Hit a wall - stop moving but DON'T change position (same as Pacman)
        movement.velocity = new Vector2(0, 0);
        ghost.currentDirection = Direction.None;

        // Select new direction when hitting wall
        const newDirection = this.selectBestDirection(
          ghost,
          gridPos,
          targetPosition
        );
        if (newDirection !== Direction.None) {
          ghost.nextDirection = newDirection;
        }
      }
    } else {
      // Not moving
      movement.velocity = new Vector2(0, 0);
    }

    // Handle tunnel wrapping (same as Pacman)
    this.handleTunnelWrap(transform);
  }

  /**
   * Get positions of nearby ghosts to avoid collisions
   */
  private getNearbyGhosts(position: Vector2): Vector2[] {
    const nearbyGhosts: Vector2[] = [];
    const allEntities = this.entityManager.getAllEntities();

    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, GhostComponent)) {
        const ghostTransform = this.componentManager.getComponent(
          entity,
          TransformComponent
        ) as TransformComponent;

        const distance = Math.sqrt(
          Math.pow(position.x - ghostTransform.position.x, 2) +
            Math.pow(position.y - ghostTransform.position.y, 2)
        );

        // If another ghost is within 40 pixels
        if (distance > 0 && distance < 40) {
          nearbyGhosts.push(ghostTransform.position);
        }
      }
    }

    return nearbyGhosts;
  }

  /**
   * Get Pinky's target position
   */
  private getPinkyTarget(
    pacmanPosition: Vector2,
    pacmanDirection: Vector2
  ): Vector2 {
    // Pinky targets 4 tiles ahead of Pacman in Pacman's direction
    // 4 tiles = 4 * 24 = 96 pixels
    return pacmanPosition.add(pacmanDirection.scale(96));
  }

  /**
   * Get Inky's target position
   */
  private getInkyTarget(
    pacmanPosition: Vector2,
    pacmanDirection: Vector2
  ): Vector2 {
    // Inky targets Blinky's position plus 2 tiles in the direction of Pacman
    const blinkyPosition = this.getBlinkyPosition();
    if (!blinkyPosition) {
      console.warn('GhostAISystem: No Blinky position found');
      return pacmanPosition.clone();
    }
    // 2 tiles = 2 * 24 = 48 pixels
    return blinkyPosition.add(pacmanDirection.scale(48));
  }

  /**
   * Get Clyde's target position
   */
  private getClydeTarget(
    ghostPosition: Vector2,
    pacmanPosition: Vector2
  ): Vector2 {
    // Clyde targets a position 8 tiles away from Pacman
    const distance = Math.sqrt(
      Math.pow(pacmanPosition.x - ghostPosition.x, 2) +
        Math.pow(pacmanPosition.y - ghostPosition.y, 2)
    );
    // 8 tiles = 8 * 24 = 192 pixels
    if (distance < 192) {
      // Run away from Pacman
      const awayDirection = ghostPosition.subtract(pacmanPosition).normalize();
      return ghostPosition.add(awayDirection.scale(192));
    } else {
      // Chase Pacman
      return pacmanPosition.clone();
    }
  }

  /**
   * Get Blinky's position
   */
  private getBlinkyPosition(): Vector2 | null {
    const allEntities = this.entityManager.getAllEntities();
    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, GhostComponent)) {
        const ghost = this.componentManager.getComponent(
          entity,
          GhostComponent
        ) as GhostComponent;
        if (ghost.ghostType === GhostType.Blinky) {
          const transform = this.componentManager.getComponent(
            entity,
            TransformComponent
          ) as TransformComponent;
          return transform.position;
        }
      }
    }
    return null;
  }

  /**
   * Get target position based on ghost type
   */
  private getGhostTarget(
    ghost: GhostComponent,
    ghostPosition: Vector2,
    pacmanPosition: Vector2
  ): Vector2 {
    switch (ghost.ghostType) {
      case GhostType.Blinky:
        return pacmanPosition.clone();
      case GhostType.Pinky:
        return this.getPinkyTarget(pacmanPosition, this.getPacmanDirection());
      case GhostType.Inky:
        return this.getInkyTarget(pacmanPosition, this.getPacmanDirection());
      case GhostType.Clyde:
        return this.getClydeTarget(ghostPosition, pacmanPosition);
      default:
        return pacmanPosition.clone();
    }
  }

  /**
   * Check if ghost should change direction
   */
  private shouldChangeDirection(
    ghost: GhostComponent,
    ghostPosition: Vector2,
    targetPosition: Vector2
  ): boolean {
    const cellSize = this.mazeData.cellSize;
    const gridPos = MazeData.pixelToGrid(
      ghostPosition.x,
      ghostPosition.y,
      cellSize
    );
    const targetGridPos = MazeData.pixelToGrid(
      targetPosition.x,
      targetPosition.y,
      cellSize
    );

    // Check if we're at a grid intersection or need to change direction
    if (gridPos.x !== targetGridPos.x && gridPos.y !== targetGridPos.y) {
      return true;
    }

    return false;
  }

  /**
   * Select best direction based on ghost type and grid position
   */
  private selectBestDirection(
    ghost: GhostComponent,
    gridPos: Vector2,
    targetPosition: Vector2
  ): Direction {
    const targetGridPos = MazeData.pixelToGrid(
      targetPosition.x,
      targetPosition.y,
      this.mazeData.cellSize
    );

    // Calculate possible directions
    const directions: Direction[] = [
      Direction.Left,
      Direction.Right,
      Direction.Up,
      Direction.Down,
    ];

    let bestDirection: Direction | null = null;
    let bestDistance = Infinity;

    for (const direction of directions) {
      const newGridPos = this.getNextGridPosition(gridPos, direction);

      // Only consider valid grid positions
      if (
        MazeData.isValidPosition(this.mazeData.maze, newGridPos.x, newGridPos.y)
      ) {
        const distance = Math.sqrt(
          Math.pow(newGridPos.x - targetGridPos.x, 2) +
            Math.pow(newGridPos.y - targetGridPos.y, 2)
        );

        if (distance < bestDistance) {
          bestDistance = distance;
          bestDirection = direction;
        }
      }
    }

    return bestDirection || Direction.None;
  }

  /**
   * Get next grid position based on direction
   */
  private getNextGridPosition(gridPos: Vector2, direction: Direction): Vector2 {
    const nextPos = gridPos.clone();

    switch (direction) {
      case Direction.Up:
        nextPos.y -= 1;
        break;
      case Direction.Down:
        nextPos.y += 1;
        break;
      case Direction.Left:
        nextPos.x -= 1;
        break;
      case Direction.Right:
        nextPos.x += 1;
        break;
    }

    return nextPos;
  }

  /**
   * Handle tunnel wrapping
   */
  private handleTunnelWrap(transform: TransformComponent): void {
    const cellSize = this.mazeData.cellSize;
    const position = transform.position;

    if (position.x < 0) {
      position.x = this.mazeData.width * cellSize - 24;
    } else if (position.x >= this.mazeData.width * cellSize) {
      position.x = 24;
    }

    if (position.y < 0) {
      position.y = this.mazeData.height * cellSize - 24;
    } else if (position.y >= this.mazeData.height * cellSize) {
      position.y = 24;
    }
  }

  /**
   * Get direction vector based on direction
   */
  private getDirectionVector(direction: Direction): Vector2 {
    switch (direction) {
      case Direction.Left:
        return new Vector2(-1, 0);
      case Direction.Right:
        return new Vector2(1, 0);
      case Direction.Up:
        return new Vector2(0, -1);
      case Direction.Down:
        return new Vector2(0, 1);
      default:
        return new Vector2(0, 0);
    }
  }
}
